/**
 * LiteQuestions — the question-stack phase of the lite self-assessment.
 * Extracted verbatim from LiteAssessment.jsx.
 */
import React from "react";
import { ArrowRight, Check, Loader2, AlertCircle } from "lucide-react";

export default function LiteQuestions({ lite, answers, setAnswers, completeness, saveState, resumedFromDraft, onShowResult }) {
  return (
    <div data-testid="lite-questions">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">
            Progress
            {resumedFromDraft ? (
              <span className="ml-2 inline-block bg-[#F4EEFD] text-[#7737F6] text-[10px] tracking-[0.14em] px-1.5 py-0.5 border border-[#7737F6]/30" data-testid="lite-resumed-pill">Resumed</span>
            ) : null}
          </span>
          <div className="flex items-center gap-3">
            {saveState === "saving" ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500" data-testid="lite-save-saving">
                <Loader2 size={12} className="animate-spin" /> Saving…
              </span>
            ) : null}
            {saveState === "saved" ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid="lite-save-saved">
                <Check size={12} /> Saved
              </span>
            ) : null}
            {saveState === "failed" ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-700" data-testid="lite-save-failed">
                <AlertCircle size={12} /> Couldn&rsquo;t save — try again shortly
              </span>
            ) : null}
            <span className="text-[12px] text-zinc-600 tabular-nums">{completeness}%</span>
          </div>
        </div>
        <div className="h-1.5 bg-zinc-100 rounded">
          <div className="h-1.5 bg-[#7737F6] rounded transition-all" style={{ width: `${completeness}%` }} />
        </div>
      </div>

      <div className="space-y-6">
        {lite.questions.map((q, qi) => (
          <div key={q.dimension_id} className="border border-zinc-200 p-5 bg-white" data-testid={`lite-q-${q.dimension_id}`}>
            <div className="text-[11px] font-medium tracking-[0.16em] text-[#7737F6] uppercase mb-1">{String(qi+1).padStart(2,"0")} · {q.dimension_label}</div>
            <p className="text-zinc-800 text-[15px] mb-3">{q.prompt}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {lite.scale.map((opt) => {
                const selected = answers[q.dimension_id] === opt.score;
                return (
                  <button
                    key={opt.score}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [q.dimension_id]: opt.score }))}
                    data-testid={`lite-q-${q.dimension_id}-opt-${opt.score}`}
                    className={`text-left flex items-start gap-3 px-3 py-2.5 text-[13px] transition-colors border ${selected ? "bg-[#7737F6] text-white border-[#7737F6]" : "bg-white border-zinc-200 hover:border-[#7737F6]"}`}
                  >
                    <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-semibold flex-shrink-0 ${selected ? "bg-white text-[#7737F6]" : "bg-zinc-100 text-zinc-700"}`}>{opt.score}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <button
          type="button"
          onClick={onShowResult}
          disabled={completeness < 100}
          className="ek-btn ek-btn-primary text-[14px] disabled:opacity-50"
          data-testid="lite-show-result-btn"
        >
          Show my maturity radar <ArrowRight size={14} />
        </button>
        {completeness < 100 ? (
          <p className="text-zinc-500 mt-3 text-[12.5px]">Answer all {lite.questions.length} questions to see your result.</p>
        ) : null}
      </div>
    </div>
  );
}
